import { useCallback, useEffect, useRef, useState } from "react";
import { UNMATCHED_PREFIX } from "@/ai-coding-outputs/evidenceUtils";
import { verdictTextOf } from "@/ai-coding-outputs/indicationHighlights";
import type { IndicationVerdictView } from "@/ai-coding-outputs/useIndicationVerdicts";

/** Half the tooltip's `max-w-xs` (320px), plus its border. */
const TOOLTIP_HALF_WIDTH = 168;

const clampToWindow = (x: number) => {
  const half = Math.min(TOOLTIP_HALF_WIDTH, window.innerWidth / 2);

  return Math.min(Math.max(x, half), window.innerWidth - half);
};

interface IndicationTooltipEntry {
  key: string;
  title: string;
  verdict: IndicationVerdictView["verdict"];
  isNotClassified: boolean;
  definition: string;
  reason: string;
  source: string;
  tags: string[];
}

interface TooltipData {
  entries: Array<{ code: string; reason?: string }>;
  indications: IndicationTooltipEntry[];
  x: number;
  y: number;
}

export function useEvidenceTooltip() {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track which highlight element we're anchored to, so we don't reposition
  // on every mousemove within the same span (prevents jitter).
  const anchoredElement = useRef<HTMLElement | null>(null);

  // Clean up pending timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  const handleMouseOver = useCallback((e: React.MouseEvent) => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);

    // Find the closest evidence-hl element from the event target
    const target = e.target as HTMLElement;
    const hlElement = target.closest(".evidence-hl, .indication-hl") as HTMLElement | null;

    if (!hlElement) {
      anchoredElement.current = null;
      setTooltip(null);
      return;
    }

    // If we're still on the same highlight element, don't reposition (prevents jitter)
    if (hlElement === anchoredElement.current) return;
    anchoredElement.current = hlElement;

    // Walk from target up to container, collecting all evidence-hl data
    const entries: Array<{ code: string; reason?: string }> = [];
    const indications: IndicationTooltipEntry[] = [];
    let el: HTMLElement | null = hlElement;
    const container = e.currentTarget as HTMLElement;

    while (el && el !== container) {
      if (el.classList.contains("evidence-hl")) {
        const code = el.getAttribute("data-code");
        const reason = el.getAttribute("data-reason") ?? undefined;
        if (code && !entries.some((entry) => entry.code === code)) {
          entries.push({ code, reason });
        }
      }
      if (el.classList.contains("indication-hl")) {
        const key = el.getAttribute("data-indication-key");
        if (key && !indications.some((entry) => entry.key === key)) {
          indications.push({
            key,
            title: el.getAttribute("data-indication-title") ?? "",
            verdict: el.getAttribute("data-indication-verdict") === "coded" ? "coded" : "not-coded",
            isNotClassified: el.getAttribute("data-indication-failed") === "true",
            definition: el.getAttribute("data-indication-definition") ?? "",
            reason: el.getAttribute("data-indication-reason") ?? "",
            source: el.getAttribute("data-indication-source") ?? "",
            tags: (el.getAttribute("data-indication-tags") ?? "").split(",").filter(Boolean),
          });
        }
      }
      el = el.parentElement;
    }

    const shownEntries =
      indications.length > 0 ? entries.filter((entry) => !entry.code.startsWith(UNMATCHED_PREFIX)) : entries;

    if (shownEntries.length === 0 && indications.length === 0) {
      setTooltip(null);
      return;
    }

    // Snap to the highlight element's top-center for stable positioning.
    // Use getClientRects() to get the first line rect (handles multi-line spans).
    const rects = hlElement.getClientRects();
    const firstRect = rects[0] ?? hlElement.getBoundingClientRect();
    setTooltip({
      entries: shownEntries,
      indications,
      x: clampToWindow(firstRect.left + firstRect.width / 2),
      y: firstRect.top,
    });
  }, []);

  const handleMouseOut = useCallback(() => {
    // Small delay to prevent flicker when moving between adjacent highlight spans
    hideTimeout.current = setTimeout(() => {
      anchoredElement.current = null;
      setTooltip(null);
    }, 100);
  }, []);

  return { tooltip, handleMouseOver, handleMouseOut };
}

const SectionHeading = ({ children }: { children: string }) => (
  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{children}</div>
);

export function EvidenceTooltip({ tooltip }: { tooltip: TooltipData | null }) {
  if (!tooltip) return null;

  const hasBoth = tooltip.entries.length > 0 && tooltip.indications.length > 0;
  return (
    <div
      className="fixed z-50 pointer-events-none animate-in fade-in-0 zoom-in-95 duration-150"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        transform: "translate(-50%, -100%) translateY(-8px)",
      }}
    >
      <div className="bg-popover border rounded-md shadow-md px-3 py-2 text-sm text-popover-foreground max-w-[min(20rem,100vw)]">
        {hasBoth && <SectionHeading>Lead term for</SectionHeading>}
        {tooltip.entries.map((entry) => {
          const isUnmatched = entry.code.startsWith(UNMATCHED_PREFIX);
          const displayCode = isUnmatched ? entry.code.slice(UNMATCHED_PREFIX.length) : entry.code;
          return (
            <div key={entry.code} className={tooltip.entries.length > 1 ? "py-0.5" : ""}>
              {isUnmatched && <span className="text-orange-600 text-xs font-medium mr-1">Unmatched term:</span>}
              <span className="font-semibold">{displayCode}</span>
              {entry.reason && <span className="text-muted-foreground ml-1.5">— {entry.reason}</span>}
            </div>
          );
        })}
        {hasBoth && (
          <>
            <div className="my-2 border-t" />
            <SectionHeading>Indication</SectionHeading>
          </>
        )}
        {tooltip.indications.map((entry) => (
          <div key={entry.key} className="py-0.5" data-testid="indication-tooltip">
            <div className="font-semibold">{entry.title}</div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="rounded bg-muted px-1 font-medium">
                {entry.isNotClassified && "⚠ "}
                {verdictTextOf(entry)}
              </span>
              {entry.tags.map((tag) => (
                <span key={tag} className="rounded bg-muted px-1">
                  {tag}
                </span>
              ))}
            </div>
            <div>{entry.reason}</div>
            {entry.definition && <div className="text-xs text-muted-foreground">{entry.definition}</div>}
            <div className="text-xs text-muted-foreground">{entry.source}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
